import { useCallback, useEffect, useState } from 'react'
import { parse, ParseResult } from 'papaparse'
import { isNaN } from 'lodash'
import { validatePhone } from 'src/lib/utils/helpers'

export enum ErrorType {
  FILE_SIZE_EXCEEDED = 'file_size_exceeded',
  CUSTOMER_PHONE_NUMBERS = 'customer_phone_numbers',
  DISCOUNT_INVALID_CODE = 'discount_invalid_code',
  REWARDS = 'rewards',
  SHIPPING = 'shipping',

  SHIPPING_INVALID_PINCODE = 'shipping_invalid_pincode',
  SHIPPING_INVALID_HEADERS = 'shipping_invalid_headers',
  SEGMENT_PINCODE_LIST = 'segment_pincode_list',
  SEGMENT_PHONE_NUMBER = 'segment_phone_number'
}

type UseCSVValidatorProps = {
  errorsToCheck: ErrorType[]
  template?: Record<string, string>
}

export const checkNaN = (value: string) => {
  return isNaN(Number(value))
}

const PINCODE_HEADER = 'Pincode'
const MINIMUM_DAYS_TO_DELIVER_HEADER = 'Minimum days to deliver'
const MAXIMUM_DAYS_TO_DELIVER_HEADER = 'Maximum days to deliver'

export const useCSVValidator = ({ errorsToCheck, template }: UseCSVValidatorProps) => {
  const [errors, setErrors] = useState<string[]>([])
  const [csvFile, setCsvFile] = useState<File>()
  const [isValidating, setIsValidating] = useState<boolean>(false)

  const checkFileSize = useCallback((file: File) => {
    const maxFileSizeInBytes = 10 * 1024 * 1024
    if (file.size > maxFileSizeInBytes) {
      return 'File size exceeds limit of 10MB'
    }
    return null
  }, [])

  const checkDiscountCustomerPhoneNumbers = useCallback((results: ParseResult<unknown>) => {
    // check headers
    const headers = results.meta?.fields
    if (!headers || headers.length !== 1) {
      return 'Please ensure there is only one column in the uploaded file'
    }

    const phoneNoInRows: string[] = results.data.map((row: any) => Object.values(row)[0] as string)
    const phoneNoInColumns: string = results.meta?.fields?.[0] || ''
    const phoneNumbers = [phoneNoInColumns, ...phoneNoInRows]

    if (phoneNumbers.length === 0) {
      return 'No phone numbers in file. Please upload a valid csv file'
    }

    const nanPhoneNumbers = phoneNumbers.filter(phone => checkNaN(phone.trim()))
    if (nanPhoneNumbers.length > 0) {
      return `Invalid phone number detected: ${nanPhoneNumbers.slice(0, 3).join(', ')}`
    }

    // const invalidPhoneNumbers = phoneNumbers
    //   .filter(phone => Boolean(phone.trim()))
    //   .filter((phone, index) => !validatePhone(phone.trim()))
    // if (invalidPhoneNumbers.length > 0) {
    //   return `Invalid phone number detected: ${invalidPhoneNumbers.slice(0, 3).join(', ')}`
    // }

    return null
  }, [])

  const checkDiscountInvalidCode = useCallback((results: ParseResult<unknown>) => {
    const headers = results.meta?.fields
    if (!headers || headers.length !== 1) {
      return 'Please ensure there is only one column in the uploaded file'
    }

    const codesInRows: string[] = results.data.map((row: any) => Object.values(row)[0] as string)
    const codesInColumns: string = results.meta?.fields?.[0] || ''
    const codes = [codesInColumns, ...codesInRows]
    if (codes.length === 0) {
      return 'No codes in file. Please upload a valid csv file'
    }

    const invalidCodes = codes.filter(code => code.includes(' '))
    if (invalidCodes.length > 0) {
      return `Please ensure there are no whitespaces in these codes: ${invalidCodes.slice(0, 3).join(', ')}`
    }

    return null
  }, [])

  const checkRewards = useCallback((results: ParseResult<unknown>) => {
    // check headers
    const headers = results.meta?.fields
    if (!headers || headers.length !== 5) {
      return 'Please ensure these headers are in the uploaded file: country_code, phone_number, credit/debit, coins_to_adjust, expiry_in_days'
    }

    // check country code
    const countyCodes = results.data.map((row: any) => Object.values(row)[0] as string).filter(Boolean)
    if (countyCodes.length === 0) {
      return 'No country codes in file. Please upload a valid csv file'
    }
    const invalidCountryCodes = countyCodes.filter(countryCode => checkNaN(countryCode))
    if (invalidCountryCodes.length > 0) {
      return `Invalid country code detected: ${invalidCountryCodes.slice(0, 3).join(', ')}`
    }

    // check phone numbers
    const phoneNumbers = results.data.map((row: any) => Object.values(row)[1] as string).filter(Boolean)
    if (phoneNumbers.length === 0) {
      return 'No phone numbers in file. Please upload a valid csv file'
    }
    const nanPhoneNumbers = phoneNumbers.filter(phone => checkNaN(phone))
    if (nanPhoneNumbers.length > 0) {
      return `Invalid phone number detected: ${nanPhoneNumbers.slice(0, 3).join(', ')}`
    }

    // const invalidPhoneNumbers = phoneNumbers
    //   .filter(phone => Boolean(phone.trim()))
    //   .filter(phone => !validatePhone(phone))
    // if (invalidPhoneNumbers.length > 0) {
    //   return `Invalid phone number detected: ${invalidPhoneNumbers.slice(0, 3).join(', ')}`
    // }

    // check debit/credit
    const debitCredit = results.data.map((row: any) => Object.values(row)[2] as string).filter(Boolean)
    const invalidRows = debitCredit.filter(
      debitCredit => debitCredit.toLowerCase() !== 'debit' && debitCredit.toLowerCase() !== 'credit'
    )
    if (invalidRows.length !== 0) {
      return 'Incorrect format of data in file: Please ensure there is a "credit" or "debit" labelled for all phone numbers'
    }

    // check coins to adjust
    // const coinsToAdjust = results.data.map((row: any) => row?.coins_to_adjust).filter(Boolean)
    // if (coinsToAdjust.length === 0) {
    //   return 'No entries under coins_to_adjust in file. Please upload a valid csv file'
    // }
    // const invalidCoinsToAdjust = coinsToAdjust.filter(coin => checkNaN(coin))
    // if (invalidCoinsToAdjust.length > 0) {
    //   return `Invalid coins_to_adjust detected: ${invalidCoinsToAdjust.slice(0, 3).join(', ')}`
    // }

    // const expiryInDays = results.data.map((row: any) => row?.expiry_in_days).filter(Boolean)
    // if (expiryInDays.length === 0) {
    //   return 'No expiry in days in file. Please upload a valid csv file'
    // }
    // const invalidExpiryInDays = expiryInDays.filter(expiry => checkNaN(expiry))
    // if (invalidExpiryInDays.length > 0) {
    //   return `Invalid expiry in days detected: ${invalidExpiryInDays.slice(0, 3).join(', ')}`
    // }

    return null
  }, [])

  const checkShipping = useCallback((results: ParseResult<unknown>) => {
    const headers = results.meta?.fields
    if (!headers || headers.length !== 3) {
      return 'Please ensure these headers are in the uploaded file: Pincode, Minimum days to deliver, Maximum days to deliver'
    }

    const unmatchedHeaders = headers.filter(
      header => !['Pincode', 'Minimum days to deliver', 'Maximum days to deliver'].includes(header)
    )
    if (unmatchedHeaders.length > 0) {
      return `Unmatched headers detected: ${unmatchedHeaders.join(
        ', '
      )}. Please ensure the headers are in the correct format`
    }

    const pincodes = results.data.map(
      (row: any) => (row[PINCODE_HEADER] as string) || (row[PINCODE_HEADER.toLowerCase()] as string)
    )

    let error = false

    results.data.forEach((row: any) => {
      if (row[MINIMUM_DAYS_TO_DELIVER_HEADER] && row[MAXIMUM_DAYS_TO_DELIVER_HEADER]) {
        if (parseInt(row[MINIMUM_DAYS_TO_DELIVER_HEADER]) >= parseInt(row[MAXIMUM_DAYS_TO_DELIVER_HEADER])) {
          error = true
        }
      }
    })

    if (error) {
      return 'Please ensure the minimum days to deliver is less than the maximum days to deliver'
    }

    const sanitizedPincodes = pincodes.filter(pincode => Boolean(pincode && pincode.trim()))
    if (sanitizedPincodes.length === 0) {
      return `No pincodes in file. Please upload a correct csv file`
    }

    const invalidPincodes = sanitizedPincodes.filter(pincode => {
      if (checkNaN(pincode)) {
        return true
      }
      if (pincode.length < 6 || pincode.length > 6) {
        return true
      }
    })

    if (invalidPincodes.length > 0) {
      return `Invalid pincode detected: ${invalidPincodes.slice(0, 3).join(', ')}`
    }

    return null
  }, [])

  const checkSegmentPhoneNumber = useCallback((results: ParseResult<unknown>) => {
    const phoneNumbers = results.data.map((row: any) => Object.values(row)[0] as string).filter(Boolean)

    if (phoneNumbers.length === 0) {
      return 'No phone numbers in file. Please upload a valid csv file'
    }

    const invalidPhoneNumbers = phoneNumbers
      .filter(phone => Boolean(phone.trim()))
      // TODO check with yashu fi any phone no can be confirmed
      .filter(phone => phone.length < 12)

    if (invalidPhoneNumbers.length > 0) {
      return `Invalid phone number detected: ${invalidPhoneNumbers.slice(0, 3).join(', ')}`
    }
    return null
  }, [])

  const checkPincodeList = useCallback((results: ParseResult<unknown>) => {
    const pincodes = results.data.map((row: any) => Object.values(row)[0] as string).filter(Boolean)
    const headers = results.meta?.fields

    if (!headers || headers.length !== 1) {
      return 'Please ensure there is only one column in the uploaded file'
    }

    const invalidPincodes = pincodes.filter(pincode => {
      if (checkNaN(pincode)) {
        return true
      }
    })
    if (invalidPincodes.length > 0) {
      return `Invalid pincode detected: ${invalidPincodes.slice(0, 3).join(', ')}`
    }
    return null
  }, [])

  const collectErrors = useCallback(
    (file: File, results: ParseResult<unknown>, errorType: ErrorType) => {
      switch (errorType) {
        // common
        case ErrorType.FILE_SIZE_EXCEEDED:
          return checkFileSize(file)

        // discount creation: bulk discount creation
        case ErrorType.DISCOUNT_INVALID_CODE:
          return checkDiscountInvalidCode(results)

        // discount creation: customer eligibility
        case ErrorType.CUSTOMER_PHONE_NUMBERS:
          return checkDiscountCustomerPhoneNumbers(results)

        // rewards creation
        case ErrorType.REWARDS:
          return checkRewards(results)

        // shipping creation
        case ErrorType.SHIPPING:
          return checkShipping(results)

        // segment creation
        case ErrorType.SEGMENT_PHONE_NUMBER:
          return checkSegmentPhoneNumber(results)

        // pincode list
        case ErrorType.SEGMENT_PINCODE_LIST:
          return checkPincodeList(results)

        default:
          return null
      }
    },
    [
      checkFileSize,
      checkDiscountCustomerPhoneNumbers,
      checkRewards,
      checkShipping,
      checkDiscountInvalidCode,
      checkSegmentPhoneNumber,
      checkPincodeList
    ]
  )

  const validateFile = useCallback(
    (file: File): Promise<string[]> => {
      return new Promise((resolve, reject) => {
        const newErrors: string[] = []

        parse(file, {
          header: true,
          complete: results => {
            errorsToCheck.forEach(errorType => {
              const error = collectErrors(file, results, errorType)
              if (error) {
                newErrors.push(error)
              }
            })
            resolve(newErrors)
          },
          error: error => {
            reject(error)
          }
        })
      })
    },
    [errorsToCheck, collectErrors]
  )

  const loadFile = useCallback(
    async (file: File) => {
      setIsValidating(true)
      setCsvFile(file)

      try {
        const validationErrors = await validateFile(file)
        setErrors(validationErrors)
        setIsValidating(false)
        return validationErrors
      } catch (error) {
        setErrors([])
        setIsValidating(false)
        throw error
      }
    },
    [validateFile]
  )

  const resetErrors = () => {
    setErrors([])
  }

  return {
    errors,
    loadFile,
    resetErrors,
    isValidating,
    validateFile
  }
}
